import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { dronesApi } from '../lib/api';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Plane, 
  Trash2, 
  Weight,
  Gauge,
  Hash,
  Pencil,
  Library
} from 'lucide-react';

const DJI_LIBRARY = [
  // Classe C0 (< 250g)
  { model: 'DJI Flip', weight: 0.249, ce_class: 'C0' },
  { model: 'DJI Mini 2 SE', weight: 0.246, ce_class: 'C0' },
  { model: 'DJI Mini 3', weight: 0.248, ce_class: 'C0' },
  { model: 'DJI Mini 3 Pro', weight: 0.248, ce_class: 'C0' },
  { model: 'DJI Mini 4 Pro', weight: 0.249, ce_class: 'C0' },
  { model: 'DJI Mini 5 Pro', weight: 0.249, ce_class: 'C0' },
  { model: 'DJI Neo', weight: 0.135, ce_class: 'C0' },
  { model: 'DJI Neo 2', weight: 0.160, ce_class: 'C0' },
  // Classe C1 (< 900g)
  { model: 'DJI Mavic 3 Classic', weight: 0.895, ce_class: 'C1' },
  { model: 'DJI Mavic 3 v2.0', weight: 0.895, ce_class: 'C1' },
  { model: 'DJI Mavic 3 Cine v2.0', weight: 0.899, ce_class: 'C1' },
  { model: 'DJI Air 2S', weight: 0.595, ce_class: 'C1' },
  { model: 'DJI Air 3', weight: 0.720, ce_class: 'C1' },
  { model: 'DJI Air 3S', weight: 0.724, ce_class: 'C1' },
  { model: 'DJI Mini 4 Pro (batterie plus)', weight: 0.342, ce_class: 'C1' },
  { model: 'DJI Avata 2', weight: 0.383, ce_class: 'C1' },
  { model: 'EVO Lite 640T Enterprise', weight: 0.866, ce_class: 'C1' },
  { model: 'EVO Lite 6K Enterprise', weight: 0.866, ce_class: 'C1' },
  { model: 'EVO Lite +', weight: 0.835, ce_class: 'C1' },
  { model: 'EVO Lite', weight: 0.835, ce_class: 'C1' },
  // Classe C2 (< 4kg)
  { model: 'Sensefly eBee', weight: 0.700, ce_class: 'C2' },
  { model: 'DJI Mavic 3E EU', weight: 0.915, ce_class: 'C2' },
  { model: 'DJI Mavic 3T EU', weight: 0.920, ce_class: 'C2' },
  { model: 'DJI Mavic 3M EU', weight: 0.920, ce_class: 'C2' },
  { model: 'DJI Mavic 3 Pro', weight: 0.958, ce_class: 'C2' },
  { model: 'EVO II v3', weight: 1.187, ce_class: 'C2' },
  { model: 'EVO II Pro v3', weight: 1.187, ce_class: 'C2' },
  { model: 'EVO II Pro Enterprise V3', weight: 1.187, ce_class: 'C2' },
  { model: 'EVO II Dual 640T V3', weight: 1.150, ce_class: 'C2' },
  { model: 'EVO II Dual 640T Enterprise V3', weight: 1.200, ce_class: 'C2' },
  { model: 'EVO II Dual 640T RTK V3', weight: 1.200, ce_class: 'C2' },
  { model: 'SenseFly eBeeX', weight: 1.400, ce_class: 'C2' },
  { model: 'EVO Max 4T', weight: 1.600, ce_class: 'C2' },
  { model: 'EVO MAX 4T Xe', weight: 1.640, ce_class: 'C2' },
  { model: 'EVO Max 4T V2', weight: 1.640, ce_class: 'C2' },
  { model: 'EVO Max 4N', weight: 1.641, ce_class: 'C2' },
  { model: 'EVO Max 4N V2', weight: 1.641, ce_class: 'C2' },
  { model: 'EVO Max 4NZ V2', weight: 1.641, ce_class: 'C2' },
  { model: 'Yuneec H520E', weight: 1.860, ce_class: 'C2' },
  { model: 'Yuneec H520E-RTK', weight: 1.860, ce_class: 'C2' },
  { model: 'DJI Matrice M30 EU', weight: 3.770, ce_class: 'C2' },
  { model: 'DJI Matrice M30T EU', weight: 3.770, ce_class: 'C2' },
  // Classe C3
  { model: 'DJI Matrice 350 RTK', weight: 9.200, ce_class: 'C3' },
  { model: 'DJI Inspire 3', weight: 3.995, ce_class: 'C3' },
  // Classe C4 (< 25kg)
  { model: 'ABZ Innovation M12', weight: 24.900, ce_class: 'C4' },
  // Classe C5 (opérations spécifiques)
  { model: 'Innovadrone 410', weight: 0.095, ce_class: 'C5' },
  { model: 'SafeAir M350 Pro', weight: 0.900, ce_class: 'C5' },
  { model: 'Chronos Mini+', weight: 1.800, ce_class: 'C5' },
  { model: 'Kronos AD Mavic 3E', weight: 1.800, ce_class: 'C5' },
  { model: 'Mavic 3E Flysafe C5', weight: 1.800, ce_class: 'C5' },
  { model: 'Kronos AD Mavic 3T', weight: 1.850, ce_class: 'C5' },
  { model: 'Kronos AD Mavic 3M', weight: 1.850, ce_class: 'C5' },
  { model: 'Mavic 3T Flysafe C5', weight: 1.850, ce_class: 'C5' },
  { model: 'Mavic 3M Flysafe', weight: 1.850, ce_class: 'C5' },
  { model: 'AVSS PRS-M3DTEX', weight: 1.900, ce_class: 'C5' },
  { model: 'Kronos AD Mavic 3 Pro Cine', weight: 1.950, ce_class: 'C5' },
  { model: 'Kronos AD Mavic 3 Pro', weight: 1.950, ce_class: 'C5' },
  { model: 'Mavic 3 Pro Cine Flysafe C5', weight: 1.950, ce_class: 'C5' },
  { model: 'Mavic 3 Pro Flysafe C5', weight: 1.950, ce_class: 'C5' },
  { model: 'Mavic 3M Flysafe C5CK-9', weight: 2.000, ce_class: 'C5' },
  { model: 'Skynnov AQ3F', weight: 3.000, ce_class: 'C5' },
  { model: 'Chronos', weight: 3.900, ce_class: 'C5' },
  { model: 'Surveyor Tethered', weight: 4.000, ce_class: 'C5' },
  { model: 'Matrice 30 Flysafe C5', weight: 4.600, ce_class: 'C5' },
  { model: 'Matrice 30T Flysafe C5', weight: 4.600, ce_class: 'C5' },
  { model: 'Inspire 3 C5', weight: 4.800, ce_class: 'C5' },
  { model: 'ATLAS', weight: 5.000, ce_class: 'C5' },
  { model: 'M350 RTK Failsafe (Flyingeye)', weight: 7.500, ce_class: 'C5' },
  { model: 'Matrice 350 RTK (Dronavia)', weight: 7.500, ce_class: 'C5' },
  { model: 'Matrice 350 RTK C5', weight: 7.500, ce_class: 'C5' },
  { model: 'AVSS PRS-M350EX', weight: 7.500, ce_class: 'C5' },
  { model: 'Spray – S', weight: 10.000, ce_class: 'C5' },
  { model: 'NDN Electric Drone', weight: 10.000, ce_class: 'C5' },
  { model: 'Agry X', weight: 12.000, ce_class: 'C5' },
  { model: 'Aeotic S-R', weight: 12.000, ce_class: 'C5' },
  { model: 'Spray – L', weight: 15.000, ce_class: 'C5' },
  { model: 'Ares', weight: 15.000, ce_class: 'C5' },
  // Classe C6 (opérations spécifiques)
  { model: 'CK25 C6', weight: 1.000, ce_class: 'C6' },
  { model: 'FireHound FH-0', weight: 1.210, ce_class: 'C6' },
  { model: 'SenseFly eBeeX C6', weight: 1.400, ce_class: 'C6' },
  { model: 'UX11 (RGB, AG, IR)', weight: 1.600, ce_class: 'C6' },
  { model: 'Delair UX11 Longue Elongation', weight: 1.600, ce_class: 'C6' },
  { model: 'AVSS PRS-M3DTEX C6', weight: 1.900, ce_class: 'C6' },
  { model: 'Guardian', weight: 2.400, ce_class: 'C6' },
  { model: 'NDN Electric Drone C6', weight: 10.000, ce_class: 'C6' },
  { model: 'Agry X C6', weight: 12.000, ce_class: 'C6' },
  { model: 'Spray – S C6', weight: 12.000, ce_class: 'C6' },
  { model: 'Spray – L C6', weight: 15.000, ce_class: 'C6' },
  { model: 'Aeotic S-R C6', weight: 15.000, ce_class: 'C6' },
  { model: 'CK23VE SW C6', weight: 25.000, ce_class: 'C6' },
];

export default function DronesPage() {
  const { t } = useTranslation();
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDrone, setEditingDrone] = useState(null);
  const [formData, setFormData] = useState({
    model_name: '',
    weight_kg: '',
    ce_class: '',
    max_altitude: '120',
    serial_number: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const CE_CLASSES = [
    { value: 'C0', label: t('drones.classes.C0') },
    { value: 'C1', label: t('drones.classes.C1') },
    { value: 'C2', label: t('drones.classes.C2') },
    { value: 'C3', label: t('drones.classes.C3') },
    { value: 'C4', label: t('drones.classes.C4') },
    { value: 'C5', label: t('drones.classes.C5') },
    { value: 'C6', label: t('drones.classes.C6') },
    { value: 'legacy', label: t('drones.classes.legacy') },
  ];

  useEffect(() => {
    loadDrones();
  }, []);

  const loadDrones = async () => {
    try {
      const response = await dronesApi.getAll();
      setDrones(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      model_name: '',
      weight_kg: '',
      ce_class: '',
      max_altitude: '120',
      serial_number: ''
    });
    setEditingDrone(null);
  };

  const openEditDialog = (drone) => {
    setEditingDrone(drone);
    setFormData({
      model_name: drone.model_name,
      weight_kg: String(drone.weight_kg),
      ce_class: drone.ce_class,
      max_altitude: String(drone.max_altitude || 120),
      serial_number: drone.serial_number || ''
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
    trackEvent('drone_form_opened');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const droneData = {
        ...formData,
        weight_kg: parseFloat(formData.weight_kg),
        max_altitude: parseInt(formData.max_altitude)
      };

      if (editingDrone) {
        await dronesApi.update(editingDrone.id, droneData);
        toast.success(t('common.success'));
      } else {
        await dronesApi.create(droneData);
        toast.success(t('common.success'));
        trackEvent('drone_added', { ce_class: droneData.ce_class || null });
      }
      
      setDialogOpen(false);
      resetForm();
      loadDrones();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (droneId) => {
    if (!window.confirm(t('drones.deleteConfirm'))) return;

    try {
      await dronesApi.delete(droneId);
      toast.success(t('common.success'));
      loadDrones();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  return (
    <DashboardLayout
      title={t('drones.title')}
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-sky-500 hover:bg-sky-600 text-white" 
              data-testid="add-drone-btn"
              onClick={openCreateDialog}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('drones.add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-[Barlow]">
                {editingDrone ? t('drones.form.editTitle') : t('drones.form.addTitle')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* DJI Library Selector */}
              {!editingDrone && (
                <div className="pb-4 border-b border-slate-200">
                  <Label className="flex items-center gap-2 mb-2">
                    <Library className="w-4 h-4 text-sky-500" />
                    {t('drones.library.title')}
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setFormData({
                          model_name: '',
                          weight_kg: '',
                          ce_class: '',
                          max_altitude: '120',
                          serial_number: ''
                        });
                      } else {
                        const selectedDrone = DJI_LIBRARY.find(d => d.model === value);
                        if (selectedDrone) {
                          setFormData({
                            ...formData,
                            model_name: selectedDrone.model,
                            weight_kg: String(selectedDrone.weight),
                            ce_class: selectedDrone.ce_class
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger data-testid="drone-library-select">
                      <SelectValue placeholder={t('drones.library.selectModel')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="custom" className="text-slate-500 italic">
                        ✏️ {t('drones.library.orManual')}
                      </SelectItem>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C0')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C0').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight * 1000}g
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C1')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C1').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight * 1000}g
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C2')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C2').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight}kg
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C3')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C3').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight}kg
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C4')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C4').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight}kg
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C5')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C5').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight}kg
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-50">
                        {t('drones.classes.C6')}
                      </div>
                      {DJI_LIBRARY.filter(d => d.ce_class === 'C6').map((drone) => (
                        <SelectItem key={drone.model} value={drone.model}>
                          {drone.model} — {drone.weight}kg
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1.5">
                    {t('drones.library.subtitle')}
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="model_name">{t('drones.fields.model')} *</Label>
                <Input
                  id="model_name"
                  value={formData.model_name}
                  onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                  placeholder={t('drones.form.modelPlaceholder')}
                  required
                  className="mt-1.5"
                  data-testid="drone-model-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight_kg">{t('drones.fields.weight')} *</Label>
                  <Input
                    id="weight_kg"
                    type="number"
                    step="0.001"
                    min="0"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                    placeholder="0.249"
                    required
                    className="mt-1.5"
                    data-testid="drone-weight-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ce_class">{t('drones.fields.class')} *</Label>
                  <Select
                    value={formData.ce_class}
                    onValueChange={(value) => setFormData({ ...formData, ce_class: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5" data-testid="drone-class-select">
                      <SelectValue placeholder={t('common.search')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CE_CLASSES.map((cls) => (
                        <SelectItem key={cls.value} value={cls.value}>
                          {cls.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_altitude">{t('missions.fields.altitude')}</Label>
                  <Input
                    id="max_altitude"
                    type="number"
                    min="0"
                    max="500"
                    value={formData.max_altitude}
                    onChange={(e) => setFormData({ ...formData, max_altitude: e.target.value })}
                    className="mt-1.5"
                    data-testid="drone-altitude-input"
                  />
                </div>
                <div>
                  <Label htmlFor="serial_number">{t('drones.fields.serialNumber')}</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder={t('drones.form.serialPlaceholder')}
                    className="mt-1.5"
                    data-testid="drone-serial-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="save-drone-btn"
                >
                  {submitting ? t('common.loading') : t('common.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      ) : drones.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Plane className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2 font-[Barlow]">
            {t('drones.empty')}
          </h3>
          <p className="text-slate-600 mb-6">
            {t('drones.emptyMessage')}
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-sky-500 hover:bg-sky-600 text-white"
            data-testid="add-first-drone-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('drones.addFirst')}
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drones.map((drone) => (
            <div
              key={drone.id}
              className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200"
              data-testid={`drone-card-${drone.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Plane className="w-6 h-6 text-sky-600" />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditDialog(drone)}
                    className="p-2 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded transition-colors"
                    data-testid={`edit-drone-${drone.id}`}
                    title={t('common.edit')}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(drone.id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    data-testid={`delete-drone-${drone.id}`}
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 mb-1 font-[Barlow]">
                {drone.model_name}
              </h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 font-mono">
                {drone.ce_class}
              </span>

              <div className="mt-4 space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Weight className="w-4 h-4 text-slate-400" />
                  <span>{drone.weight_kg} kg</span>
                </div>
                <div className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-slate-400" />
                  <span>Max {drone.max_altitude || 120}m</span>
                </div>
                {drone.serial_number && (
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-slate-400" />
                    <span className="font-mono text-xs">{drone.serial_number}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
